<template>
    <div class="sovt__charts" :class="{ hidden: !isSynodicOrbitChartAcive, inverted: isThemeInverted }">
        <p class="sovt__title">{{ $t.charts }}</p>
        <p class="sovt__charts-info" v-show="!selectedCalculatedObject">
            {{ $t.invisibleCharts }}
        </p>
        <SynodicOrbitChartsSwitcher v-show="selectedCalculatedObject" />
        <div class="sovt__charts-container" v-show="selectedCalculatedObject">
            <div class="sovt__chart sovt__chart--earth-distance">
                <canvas id="earthDistanceChart"></canvas>
            </div>
            <div class="sovt__chart sovt__chart--phase-angle">
                <canvas id="phaseAngleChart"></canvas>
            </div>
            <div class="sovt__chart sovt__chart--magnitude">
                <canvas id="magnitudeChart"></canvas>
            </div>
        </div>
    </div>
</template>

<script>
import * as ChartJs from "chart.js";
import moment from "moment";
import "chartjs-adapter-moment";
import annotationPlugin from "chartjs-plugin-annotation";
import SynodicOrbitChartsSwitcher from "@/components/sovt/SynodicOrbitChartsSwitcher";
import SynodicCalculationService from "@/services/synodic-calculation.service";
import SynodicChartService from "@/services/synodic-chart.service";
import TimeFrameService from "@/services/timeframe.service";
import VisualisationService from "@/services/visualisation.service";

const PHASE_ANGLE_SERIES_COLOR = "#0F84DD";
const ELONGATION_SERIES_COLOR = "#FDC82F";

const multiColorYAxisTitlePlugin = {
    id: "multiColorYAxisTitle",
    afterDraw(chart, _args, pluginOptions) {
        const opts = pluginOptions || {};
        if (!opts.enabled) {
            return;
        }
        const yScale = chart.scales && chart.scales.y;
        if (!yScale || !Array.isArray(opts.parts) || !opts.parts.length) {
            return;
        }

        const { ctx } = chart;
        const fontFamily = SynodicChartService.FONT_FAMILY;
        const fontWeight = opts.fontWeight || "normal";
        const gap = opts.gap ?? 2;

        ctx.save();
        ctx.font = `${fontWeight} ${opts.fontSize}px ${fontFamily}`;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";

        const widths = opts.parts.map((part) => ctx.measureText(part.text).width);
        const totalWidth =
            widths.reduce((sum, width) => sum + width, 0) +
            Math.max(0, opts.parts.length - 1) * gap;

        // Keep the custom Y title inside the canvas; long titles + narrow charts can push it outside.
        const minX = opts.fontSize + 4;
        const centerX =
            typeof opts.fixedX === "number"
                ? opts.fixedX
                : Math.max(minX, yScale.left - opts.offset);
        const centerY = (yScale.top + yScale.bottom) / 2 + opts.yShift;

        ctx.translate(centerX, centerY);
        ctx.rotate(-Math.PI / 2);

        let cursorX = -totalWidth / 2;
        opts.parts.forEach((part, index) => {
            ctx.fillStyle = part.color || "#fff";
            ctx.fillText(part.text, cursorX, 0);
            cursorX += widths[index] + gap;
        });
        ctx.restore();
    },
};

const centerLegendOnPlotAreaPlugin = {
    id: "centerLegendOnPlotArea",
    afterLayout(chart, _args, pluginOptions) {
        const opts = pluginOptions || {};
        if (!opts.enabled || !chart.legend || !chart.chartArea) {
            return;
        }

        const legend = chart.legend;
        const chartAreaCenter = (chart.chartArea.left + chart.chartArea.right) / 2;
        const legendCenter = (legend.left + legend.right) / 2;
        const dx = Math.round(chartAreaCenter - legendCenter);

        if (!dx) {
            return;
        }

        legend.left += dx;
        legend.right += dx;

        if (Array.isArray(legend.legendHitBoxes)) {
            legend.legendHitBoxes.forEach((hitBox) => {
                hitBox.left += dx;
            });
        }
    },
};

export default {
    name: "SynodicOrbitCharts",
    components: {
        SynodicOrbitChartsSwitcher,
    },
    data() {
        this.magnitudeChart = null;
        this.earthDistanceChart = null;
        this.phaseAngleChart = null;
        const phaseAngleTitle = `${this.$t.phaseAngle} (${this.$t.degrees})`;
        const elongationTitle = `${this.$t.solarElongation} (${this.$t.degrees})`;
        const phaseAngleChartConfig = SynodicChartService.generateChartConfig({
            y: {
                title: `${phaseAngleTitle} / ${elongationTitle}`,
            },
            x: {
                title: `${this.$t.time
                    } (${this.$t.timeUnit_d.toLowerCase()})`,
            },
            plugins: {
                legendVisible: true,
            },
        });
        phaseAngleChartConfig.options.scales.y.title.display = false;
        phaseAngleChartConfig.options.plugins.legend = {
            display: true,
            position: "top",
            align: "center",
            labels: {
                boxWidth: 30,
                boxHeight: 1,
            },
        };
        phaseAngleChartConfig.options.plugins.tooltip = {
            ...phaseAngleChartConfig.options.plugins.tooltip,
            mode: "nearest",
            intersect: true,
            callbacks: {
                title: (items) => {
                    if (!items?.length) {
                        return "";
                    }
                    const timeLabel =
                        items[0].chart.options.scales.x.title.text || "";
                    const timeValue = SynodicChartService.convertTooltipDate(
                        items[0].label
                    );
                    return `${timeLabel}: ${timeValue}`;
                },
                label: (ctx) => {
                    const value = Number.isFinite(ctx.parsed.y)
                        ? ctx.parsed.y.toFixed(3)
                        : "-";
                    return `${ctx.dataset.label}: ${value}`;
                },
            },
        };
        phaseAngleChartConfig.options.plugins.multiColorYAxisTitle = {
            enabled: true,
            fixedX: 10,
            offset: 30,
            yShift: -5,
            gap: 2,
            parts: [
                { text: phaseAngleTitle, color: PHASE_ANGLE_SERIES_COLOR },
                { text: " / ", color: "#FFFFFF" },
                { text: elongationTitle, color: ELONGATION_SERIES_COLOR },
            ],
        };
        phaseAngleChartConfig.options.plugins.centerLegendOnPlotArea = {
            enabled: true,
        };
        return {
            earthDistanceChartConfig: SynodicChartService.generateChartConfig({
                y: {
                    title: `${this.$t.earthDistance} (${this.$t.au})`,
                },
                x: {
                    title: `${this.$t.time
                        } (${this.$t.timeUnit_d.toLowerCase()})`,
                },
            }),
            phaseAngleChartConfig,
            magnitudeChartConfig: SynodicChartService.generateChartConfig({
                y: {
                    title: `${this.$t.visualMagnitude}`,
                    reverse: true,
                },
                x: {
                    title: `${this.$t.time
                        } (${this.$t.timeUnit_d.toLowerCase()})`,
                },
            }),
            chartLineDateValue: null,
        };
    },
    computed: {
        settings() {
            return VisualisationService.getSettings();
        },

        isSynodicOrbitChartAcive() {
            return this.settings && this.settings.synodicOrbitCharts;
        },

        selectedCalculatedObject() {
            return SynodicCalculationService.getSelectedCalculatedObject();
        },

        perturbedSynodicOrbitList() {
            return SynodicCalculationService.getPerturbedSynodicOrbitList();
        },

        isSynodicChartsDaysMode() {
            return SynodicChartService.getSynodicOrbitChartsSettings()
                .daysNumberMode;
        },

        selectedTimeframe() {
            return TimeFrameService.getTimeFrame().converted;
        },

        simulationTime() {
            return TimeFrameService.getSimulationTime();
        },

        getChartsDataInfo() {
            return SynodicChartService.getSynodicOrbitChartsData().objects;
        },

        timeAxisTitle() {
            return `${this.$t.time} (${this.isSynodicChartsDaysMode
                    ? this.$t.timeUnit_d.toLowerCase()
                    : this.$t.timeUnit_date.toLowerCase()
                })`;
        },

        timeAxisValues() {
            const { date } = SynodicChartService.getSynodicOrbitChartsData();
            return date.map((date, index) =>
                this.isSynodicChartsDaysMode ? index : date
            );
        },

        timeAxisType() {
            return this.isSynodicChartsDaysMode ? "linear" : "time";
        },

        isThemeInverted() {
            return VisualisationService.getInvertColors();
        },
    },
    methods: {
        getDefaultChartTicks(stepSize) {
            return {
                stepSize,
                callback(value) {
                    return Number.isFinite(value)
                        ? parseFloat(value.toFixed(2))
                        : "0";
                },
                minRotation: 0,
                maxRotation: 0,
            };
        },

        getPhaseChartDatasetVisibility(chart = this.phaseAngleChart) {
            const phaseDataset = chart?.data?.datasets?.[0];
            const elongationDataset = chart?.data?.datasets?.[1];
            return {
                phaseVisible: !phaseDataset || chart.isDatasetVisible(0),
                elongationVisible: !elongationDataset || chart.isDatasetVisible(1),
            };
        },

        setPointAnnotation(annotation, xValue, yValue, isVisible) {
            if (!annotation) {
                return;
            }
            annotation.display = isVisible;
            if (!isVisible) {
                return;
            }
            annotation.xValue = xValue;
            annotation.yValue = yValue;
        },

        updatePhaseChartYAxis(chart) {
            const { phaseVisible, elongationVisible } = this.getPhaseChartDatasetVisibility(chart);
            const phaseValues =
                phaseVisible && chart.data.datasets[0]?.data
                    ? chart.data.datasets[0].data
                          .map(({ y }) => y)
                          .filter(Number.isFinite)
                    : [];
            const elongationValues =
                elongationVisible && chart.data.datasets[1]?.data
                    ? chart.data.datasets[1].data
                          .map(({ y }) => y)
                          .filter(Number.isFinite)
                    : [];
            const areBothHidden = !phaseValues.length && !elongationValues.length;
            if (areBothHidden) {
                chart.options.scales.y.beginAtZero = true;
                chart.options.scales.y.min = 0;
                chart.options.scales.y.max = 90;
                chart.options.scales.y.reverse = false;
                chart.options.scales.y.ticks = this.getDefaultChartTicks(30);
                return;
            }

            const valuesForAxis =
                phaseValues.length && elongationValues.length
                    ? [...phaseValues, ...elongationValues]
                    : phaseValues.length
                        ? phaseValues
                        : elongationValues;
            const minValue = Math.min(...valuesForAxis);
            const maxValue = Math.max(...valuesForAxis);
            const { step } = SynodicChartService.setUpChartAxisValues(valuesForAxis, true, valuesForAxis.length <= 1);
            const baseLowerBound = Math.floor(minValue / step) * step;
            const baseUpperBound = Math.ceil(maxValue / step) * step;
            const outerPadding = Math.round(step * 0.25);
            chart.options.scales.y.beginAtZero = false;
            chart.options.scales.y.min = baseLowerBound - outerPadding;
            chart.options.scales.y.max = baseUpperBound + outerPadding;
            chart.options.scales.y.ticks = this.getDefaultChartTicks(step);
        },

        updatePhaseChartDateLine(chart, date) {
            const annotations = chart.options.plugins.annotation.annotations;

            if (date === null || date === undefined || date === -1) {
                annotations.lineAnnotation.display = false;
                this.setPointAnnotation(annotations.pointAnnotation, date, 0, false);
                this.setPointAnnotation(annotations.secondaryPointAnnotation, date, 0, false);
                return;
            }

            const { phaseVisible, elongationVisible } = this.getPhaseChartDatasetVisibility(chart);
            const phaseValue = phaseVisible
                ? SynodicChartService.getValueForCurrentDate(
                      chart.data.datasets[0]?.data || [],
                      date
                  )
                : null;
            const elongationValue = elongationVisible
                ? SynodicChartService.getValueForCurrentDate(
                      chart.data.datasets[1]?.data || [],
                      date
                  )
                : null;

            annotations.lineAnnotation.display = true;
            annotations.lineAnnotation.value = date;
            this.setPointAnnotation(annotations.pointAnnotation, date, phaseValue, phaseVisible && phaseValue !== null);
            this.setPointAnnotation(annotations.secondaryPointAnnotation, date, elongationValue, elongationVisible && elongationValue !== null);
        },

        updateCharts(selectedObject) {
            const chartsMap = this.getChartsData(selectedObject);
            if (!chartsMap) {
                return;
            }
            const dataNumber = chartsMap.magnitude.value.length;
            const currentDateValue = this.getCurrentDateChartValue();
            const {
                step: timeAxisStep,
            } = SynodicChartService.setUpChartAxisValues(
                [0, dataNumber],
                false,
                dataNumber === 1
            );
            Object.keys(chartsMap).forEach((chartInfo) => {
                const { label, value } = chartsMap[chartInfo];
                const [chart] = this.getChart(label);
                const isPhaseChart = chartInfo === "phaseAngle";
                this.updateChartTimeAxisValues(chart, timeAxisStep);
                const dataPoints = value.map((v, index) => ({
                    x: this.timeAxisValues[index],
                    y: Number.isFinite(v) ? parseFloat(v.toFixed(2)) : 0, // Prevent formatting errors
                }));
                if (isPhaseChart) {
                    const { phaseVisible, elongationVisible } = this.getPhaseChartDatasetVisibility(chart);
                    const elongationPoints = (chartsMap[chartInfo].elongationValue || []).map((v, index) => ({
                        x: this.timeAxisValues[index],
                        y: Number.isFinite(v) ? parseFloat(v.toFixed(2)) : 0,
                    }));
                    chart.data.datasets = [
                        {
                            label: this.$t.phaseAngle,
                            data: dataPoints,
                            borderColor: PHASE_ANGLE_SERIES_COLOR,
                            borderWidth: 1,
                            pointRadius: 0,
                            hidden: !phaseVisible,
                        },
                        {
                            label: this.$t.solarElongation,
                            data: elongationPoints,
                            borderColor: ELONGATION_SERIES_COLOR,
                            borderWidth: 1,
                            pointRadius: 0,
                            hidden: !elongationVisible,
                        },
                    ];
                    this.updatePhaseChartYAxis(chart);
                    this.updatePhaseChartDateLine(chart, currentDateValue);
                } else {
                    const {
                        step: valueStep,
                        startValue,
                    } = SynodicChartService.setUpChartAxisValues(
                        value.length ? value : [0],
                        true,
                        value.length <= 1
                    );
                    chart.options.scales.y.ticks = this.getDefaultChartTicks(valueStep);
                    chart.data.datasets[0] = { data: dataPoints, ...(chartInfo === 'magnitude' && {originalData: chartsMap[chartInfo].originalValue}) };
                    chart.options.scales.y.beginAtZero = !(startValue !== 0);
                    chart.options.scales.y.min = startValue;
                    chart.options.scales.y.max = null;
                    chart.options.scales.y.reverse = chartInfo === "magnitude";
                    this.updateChartDateLine(chart, dataPoints, currentDateValue);
                    if (chartInfo === 'magnitude') {
                        const magnitudeMaxValue = Math.max(...chartsMap[chartInfo].originalValue);
                        chart.options.scales.y.max = magnitudeMaxValue > 32 ? 32 : null;
                    }
                }
                chart.update();
            });
        },

        updateChartsTimeAxis() {
            const chartsMap = this.getChartsData(this.selectedCalculatedObject);
            const dataNumber = chartsMap.magnitude.value.length;
            const currentDateValue = this.getCurrentDateChartValue();
            const {
                step: timeAxisStep,
            } = SynodicChartService.setUpChartAxisValues(
                [0, dataNumber],
                false,
                dataNumber === 1
            );
            [
                this.magnitudeChart,
                this.earthDistanceChart,
                this.phaseAngleChart,
            ].forEach((chart, idx) => {
                const isMagnitudeChart = idx === 0;
                const isPhaseChart = idx === 2;
                this.updateChartTimeAxisValues(chart, timeAxisStep);
                const dataPoints = chart.data.datasets[0].data.map(
                    ({ y }, index) => ({
                        x: this.timeAxisValues[index],
                        y: Number.isFinite(y) ? parseFloat(y.toFixed(2)) : 0 // Ensure safe number formatting
                    })
                );
                chart.data.datasets[0] = { ...chart.data.datasets[0], data: dataPoints, ...(isMagnitudeChart && {originalData: chartsMap.magnitude.originalValue}) };
                if (isPhaseChart && chart.data.datasets[1]) {
                    const elongationPoints = chart.data.datasets[1].data.map(
                        ({ y }, index) => ({
                            x: this.timeAxisValues[index],
                            y: Number.isFinite(y) ? parseFloat(y.toFixed(2)) : 0
                        })
                    );
                    chart.data.datasets[1] = { ...chart.data.datasets[1], data: elongationPoints };
                    this.updatePhaseChartYAxis(chart);
                    this.updatePhaseChartDateLine(chart, currentDateValue);
                } else {
                    this.updateChartDateLine(chart, dataPoints, currentDateValue);
                }
                chart.update();
            });
        },

        updateCurrentDate() {
            const currentDateValue = this.getCurrentDateChartValue();
            if (currentDateValue === this.chartLineDateValue) {
                return;
            }
            [
                this.magnitudeChart,
                this.earthDistanceChart,
                this.phaseAngleChart,
            ].forEach((chart) => {
                if (chart === this.phaseAngleChart) {
                    this.updatePhaseChartDateLine(chart, currentDateValue);
                } else {
                    this.updateChartDateLine(
                        chart,
                        chart.data.datasets[0].data,
                        currentDateValue
                    );
                }
                chart.update();
            });
            this.chartLineDateValue = currentDateValue;
        },

        updateChartDateLine(chart, values, date) {
            const valueForDate = SynodicChartService.getValueForCurrentDate(
                values,
                date
            );
            if (valueForDate === null) {
                this.toggleDateAnnotationVisibility(chart, false);
                return;
            }
            this.toggleDateAnnotationVisibility(chart);
            chart.options.plugins.annotation.annotations.lineAnnotation.value = date;
            chart.options.plugins.annotation.annotations.pointAnnotation.xValue = date;
            chart.options.plugins.annotation.annotations.pointAnnotation.yValue = valueForDate;
        },

        updateChartTimeAxisValues(chart, timeAxisStep) {
            chart.options.scales.x.title.text = this.timeAxisTitle;
            chart.options.scales.x.type = this.timeAxisType;
            chart.options.scales.x.min = this.timeAxisValues[0];
            chart.options.scales.x.max = this.timeAxisValues[this.timeAxisValues.length - 1];
            if (chart === this.phaseAngleChart && chart.options.plugins?.multiColorYAxisTitle) {
                chart.options.plugins.multiColorYAxisTitle.fontSize = this.isSynodicChartsDaysMode ? 12 : 11;
            }
            chart.options.scales.x.time = this.isSynodicChartsDaysMode
                ? {}
                : {
                    ...SynodicChartService.TIME_DISPLAY_SETTINGS,
                    stepSize: timeAxisStep,
                };
            chart.options.scales.x.ticks = {
                ...chart.options.scales.x.ticks,
                stepSize: this.isSynodicChartsDaysMode ? timeAxisStep : null,
                ...(this.isSynodicChartsDaysMode
                    ? SynodicChartService.DAY_TICKS_SETTINGS
                    : SynodicChartService.DATE_TICKS_SETTINGS),
            };
        },

        toggleDateAnnotationVisibility(chart, isVisible = true) {
            if (
                isVisible ===
                chart.options.plugins.annotation.annotations.lineAnnotation
                    .display
            ) {
                return;
            }
            chart.options.plugins.annotation.annotations.lineAnnotation.display = isVisible;
            chart.options.plugins.annotation.annotations.pointAnnotation.display = isVisible;
        },

        getCurrentDateChartValue() {
            const currentDateString = moment
                .utc(TimeFrameService.getSimulationTime())
                .format("YYYY-MM-DD");
            const currentTimestamp = moment.utc(
                currentDateString,
                "YYYY-MM-DD"
            ).valueOf();
            const { date } = SynodicChartService.getSynodicOrbitChartsData();
            return this.isSynodicChartsDaysMode
                ? date.indexOf(currentTimestamp)
                : currentTimestamp;
        },

        getChartsData(selectedObject) {
            const selectedObjectDesignator = this.getSelectedObjectDesignator(
                selectedObject
            );
            const { objects } = SynodicChartService.getSynodicOrbitChartsData();
            if (
                !selectedObjectDesignator ||
                !objects[selectedObjectDesignator]
            ) {
                return null;
            }
            const { apparentMag, earthDistance, phaseAngle, elongationAngle, apparentMagOriginalValues } = objects[
                selectedObjectDesignator
            ];
            return {
                magnitude: {
                    label: "magnitudeChart",
                    value: apparentMag,
                    originalValue: apparentMagOriginalValues,
                },
                earthDistance: {
                    label: "earthDistanceChart",
                    value: earthDistance,
                },
                phaseAngle: {
                    label: "phaseAngleChart",
                    value: phaseAngle,
                    elongationValue: elongationAngle || [],
                },
            };
        },

        getChart(chartName = null) {
            const charts = {
                magnitudeChart: this.magnitudeChart,
                earthDistanceChart: this.earthDistanceChart,
                phaseAngleChart: this.phaseAngleChart,
            };
            return !chartName ? Object.values(charts) : [charts[chartName]];
        },

        getSelectedObjectDesignator(selectedObject) {
            if (!selectedObject) {
                return null;
            }
            const isPerturbedOrbit = SynodicCalculationService.getPerturbedSynodicOrbitList().includes(
                selectedObject.designator
            );
            return isPerturbedOrbit
                ? `${selectedObject.designator}_perturbed`
                : selectedObject.designator;
        },
    },
    watch: {
        selectedCalculatedObject(selectedObject) {
            if (!selectedObject) {
                console.warn("No selected object found for charts update.");
                return;
            }
            try {
                const selectedObjectDesignator = this.getSelectedObjectDesignator(selectedObject);
                if (!selectedObjectDesignator || !this.getChartsDataInfo[selectedObjectDesignator]) {
                    console.warn("No chart data available for", selectedObjectDesignator);
                    return;
                }
             this.updateCharts(selectedObject);
            } catch (error) {
                console.error("Error updating charts for selected object:", error);
            }
        },

        getChartsDataInfo: {
            deep: true,
            handler(chartData) {
                if (!chartData || !this.selectedCalculatedObject) {
                    console.warn("Chart data or selected object is missing.");
                    return;
                }
                try {
                    const selectedObjectDesignator = this.getSelectedObjectDesignator(this.selectedCalculatedObject);
                    if (!selectedObjectDesignator || !chartData[selectedObjectDesignator]) {
                        console.warn("No chart data available for", selectedObjectDesignator);
                        return;
                    }
                    this.updateCharts(this.selectedCalculatedObject);
                } catch (error) {
                    console.error("Error handling chart data update:", error);
                }
            },
        },

        perturbedSynodicOrbitList() {
            if (!this.selectedCalculatedObject) {
                console.warn("No selected object for perturbed orbit update.");
                return;
            }
            try {
                this.updateCharts(this.selectedCalculatedObject);
            } catch (error) {
                console.error("Error updating perturbed orbit charts:", error);
            }
        },

        isSynodicChartsDaysMode() {
            try {
                this.updateChartsTimeAxis();
            } catch (error) {
                console.error("Error updating chart time axis:", error);
            }
        },

        simulationTime() {
            if (!this.selectedCalculatedObject || !this.isSynodicOrbitChartAcive) {
                return;
            }
            try {
                this.updateCurrentDate();
            } catch (error) {
                console.error("Error updating chart date:", error);
            }
        },

    isSynodicOrbitChartAcive(isActive) {
        if (!this.selectedCalculatedObject || !isActive) {
            return;
        }
        try {
            this.updateCurrentDate();
        } catch (error) {
            console.error("Error updating active chart date:", error);
        }
    },
    },
        mounted() {
        ChartJs.Chart.register(annotationPlugin);
        ChartJs.Chart.register(multiColorYAxisTitlePlugin);
        ChartJs.Chart.register(centerLegendOnPlotAreaPlugin);
        ChartJs.Chart.register.apply(
            null,
            Object.values(ChartJs).filter((chartClass) => chartClass.id)
        );
        this.magnitudeChart = new ChartJs.Chart(
            document.querySelector("#magnitudeChart"),
            this.magnitudeChartConfig
        );
        this.earthDistanceChart = new ChartJs.Chart(
            document.querySelector("#earthDistanceChart"),
            this.earthDistanceChartConfig
        );
        this.phaseAngleChart = new ChartJs.Chart(
            document.querySelector("#phaseAngleChart"),
            this.phaseAngleChartConfig
        );
        const defaultLegendClick = ChartJs.Chart.defaults.plugins.legend.onClick;
        this.phaseAngleChart.options.plugins.legend.onClick = (
            event,
            legendItem,
            legend
        ) => {
            defaultLegendClick(event, legendItem, legend);
            this.updatePhaseChartYAxis(this.phaseAngleChart);
            this.updatePhaseChartDateLine(this.phaseAngleChart, this.getCurrentDateChartValue());
            this.phaseAngleChart.update();
        };
        this.selectedCalculatedObject &&
            this.updateCharts(this.selectedCalculatedObject);
    },
};
</script>

<style lang="scss">
@import "@/styles/sovt/charts.scss";
</style>
